import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Calendar, Users, Trash2, Eye, Clock, Plane, Hotel, ExternalLink, Wallet
} from 'lucide-react';
import { format } from 'date-fns';

const getTravelerIcon = (type) => {
  switch (type) {
    case 'Solo': return '\u{1F9F3}';
    case 'Couple': return '\u{1F491}';
    case 'Family': return '\u{1F468}\u200D\u{1F469}\u200D\u{1F467}\u200D\u{1F466}';
    case 'Friends': return '\u{1F46F}';
    default: return '\u2708\uFE0F';
  }
};

const getStatusBadge = (status) => {
  if (status === 'completed') {
    return (
      <span className="px-2 py-1 bg-gold text-ocean text-xs rounded-full font-bold shadow-sm">
        Ready
      </span>
    );
  }
  return (
    <span className="px-2 py-1 bg-burnt/10 text-burnt text-xs rounded-full font-medium">
      Pending
    </span>
  );
};

export function TripCard({ itinerary, index, onDelete }) {
  const navigate = useNavigate();

  const handleBookFlight = (e) => {
    e.stopPropagation();
    try {
      const ti = itinerary.trip_input;
      const origin = ti.destination_country || 'US';
      const destination = ti.destination_city;
      const startDate = format(new Date(ti.start_date), 'yyyy-MM-dd');
      const adults = ti.adults || 1;
      const expediaUrl = `https://www.expedia.com/Flights-Search?leg1=from:${encodeURIComponent(origin)},to:${encodeURIComponent(destination)},departure:${startDate}TANYT&passengers=adults:${adults},children:0,infantinlap:N&options=cabinClass:coach&mode=search&pageId=0`;
      window.open(expediaUrl, '_blank', 'noopener,noreferrer');
      toast.success('Opening Expedia Flights...');
    } catch {
      toast.error('Unable to open booking site');
    }
  };

  const handleBookLodging = (e) => {
    e.stopPropagation();
    try {
      const ti = itinerary.trip_input;
      const destination = ti.destination_city;
      const checkIn = format(new Date(ti.start_date), 'yyyy-MM-dd');
      const checkOut = format(new Date(ti.end_date), 'yyyy-MM-dd');
      const adults = ti.adults || 1;
      const bookingUrl = `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(destination)}&checkin=${checkIn}&checkout=${checkOut}&group_adults=${adults}&no_rooms=1`;
      window.open(bookingUrl, '_blank', 'noopener,noreferrer');
      toast.success('Opening Booking.com...');
    } catch {
      toast.error('Unable to open booking site');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <Card
        className="bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl overflow-hidden cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
        onClick={() => navigate(`/itinerary/${itinerary.id}`)}
        data-testid={`itinerary-card-${itinerary.id}`}
      >
        <CardContent className="p-0">
          <div className="h-24 bg-gradient-to-br from-ocean via-ocean-light to-sky relative">
            <div className="absolute top-4 right-4">
              {getStatusBadge(itinerary.status)}
            </div>
            <div className="absolute -bottom-6 left-4">
              <div className="w-12 h-12 bg-white rounded-lg shadow-md flex items-center justify-center text-2xl">
                {getTravelerIcon(itinerary.trip_input.traveler_type)}
              </div>
            </div>
          </div>

          <div className="pt-10 pb-4 px-4">
            <h3 className="font-serif text-xl text-charcoal mb-1 truncate">
              {itinerary.trip_input.destination_city}
            </h3>
            <p className="text-charcoal/60 text-sm mb-4">
              {itinerary.trip_input.destination_country}
            </p>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-charcoal/70">
                <Calendar className="w-4 h-4" />
                <span className="font-mono">
                  {itinerary.trip_input.start_date} &rarr; {itinerary.trip_input.end_date}
                </span>
              </div>
              <div className="flex items-center gap-2 text-charcoal/70">
                <Users className="w-4 h-4" />
                <span>
                  {itinerary.trip_input.adults} adults
                  {itinerary.trip_input.kids > 0 && `, ${itinerary.trip_input.kids} kids`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-charcoal/70">
                <Clock className="w-4 h-4" />
                <span>
                  Created {format(new Date(itinerary.created_at), 'MMM d, yyyy')}
                </span>
              </div>
            </div>

            <div className="space-y-2 mt-4 pt-4 border-t border-sand-dark">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" className="flex-1 text-forest hover:bg-forest/10"
                  onClick={(e) => { e.stopPropagation(); navigate(`/itinerary/${itinerary.id}`); }}
                  data-testid={`view-btn-${itinerary.id}`}>
                  <Eye className="w-4 h-4 mr-1" /> View
                </Button>
                <Button variant="ghost" size="sm" className="flex-1 text-ocean hover:bg-ocean/10"
                  onClick={(e) => { e.stopPropagation(); navigate(`/itinerary/${itinerary.id}/budget`); }}
                  data-testid={`budget-btn-${itinerary.id}`}>
                  <Wallet className="w-4 h-4 mr-1" /> Budget Tracker
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10"
                  onClick={(e) => { e.stopPropagation(); onDelete(itinerary.id); }}
                  data-testid={`delete-btn-${itinerary.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              {itinerary.status === 'completed' && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="flex-1 text-sky border-sky/30 hover:bg-sky/10 text-xs"
                    onClick={handleBookFlight} data-testid={`flight-btn-${itinerary.id}`}>
                    <Plane className="w-3 h-3 mr-1" /> Flight <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-gold border-gold/30 hover:bg-gold/10 text-xs"
                    onClick={handleBookLodging} data-testid={`lodging-btn-${itinerary.id}`}>
                    <Hotel className="w-3 h-3 mr-1" /> Lodging <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
